import React, { useState } from 'react';
import axios from 'axios';
import { API, Badge, Field, SelectField, TextField, errText, formValues, propName, useHeaders, useLoad, usePortal, useSubmit } from './kit';

const METHOD = { call: 'Call', text: 'Text', email: 'Email' };
const scopeLabel = (c, properties) => (c.scope === 'all' ? 'All properties'
  : c.address_ids.map((id) => propName(properties.find((p) => p.id === id))).join(', '));

const ContactEditor = ({ contact, scope, properties, onSaved }) => {
  const headers = useHeaders();
  const { toast, closeDrawer } = usePortal();
  const [busy, run] = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    const f = formValues(e);
    const phone = (f.phone || '').trim();
    const email = (f.email || '').trim();
    if (!phone && !email) return toast('Add at least one phone number or email address.');
    if ((f.preferred === 'email' && !email) || (f.preferred !== 'email' && !phone)) return toast('Add the phone or email needed for the preferred contact method.');
    const body = { full_name: f.full_name.trim(), relationship: f.relationship.trim(), phone, email, preferred_method: f.preferred,
      scope: f.applies === 'all' ? 'all' : 'properties', address_ids: f.applies === 'all' ? [] : [f.applies], instructions: f.instructions || '', share_permission: !!f.permission };
    return run(async () => {
      try {
        if (contact) await axios.put(`${API}/api/portal/contacts/${contact.id}`, body, { headers });
        else await axios.post(`${API}/api/portal/contacts`, body, { headers });
        toast('Contact saved. No message was sent to them.'); closeDrawer(); onSaved();
      } catch (err) { toast(errText(err)); }
    });
  };
  const applies = contact ? (contact.scope === 'all' ? 'all' : contact.address_ids[0]) : scope || 'all';
  return (
    <form onSubmit={submit} data-testid="contact-form">
      <Field label="Full name" name="full_name" defaultValue={contact?.full_name} required maxLength={100} />
      <Field label="Relationship / role" name="relationship" defaultValue={contact?.relationship} required maxLength={80} placeholder="Spouse, co-host, property manager…" />
      <Field label="Phone" name="phone" type="tel" defaultValue={contact?.phone} maxLength={30} autoComplete="tel" />
      <Field label="Email" name="email" type="email" defaultValue={contact?.email} maxLength={150} autoComplete="email" />
      <p className="muted">Provide a phone number, an email address, or both.</p>
      <SelectField label="Preferred contact" name="preferred" defaultValue={contact?.preferred_method || 'call'} options={Object.entries(METHOD)} />
      <SelectField label="Applies to" name="applies" defaultValue={applies} options={[['all', 'All properties'], ...properties.map((p) => [p.id, propName(p)])]} />
      <TextField label="When should we contact this person?" name="instructions" defaultValue={contact?.instructions} maxLength={1000} placeholder="If I don’t answer on cleaning day, call my spouse. Available after 10 AM." />
      <label className="check"><input name="permission" type="checkbox" required defaultChecked={contact?.share_permission} data-testid="field-permission" />
        <span>I have this person’s permission to share their details with Gleam Guru for service-related contact.</span></label>
      <p className="muted">Contact preference is not marketing consent.</p>
      <button className="primary" type="submit" disabled={busy} data-testid="contact-save">{busy ? 'Saving…' : 'Save contact'}</button>
    </form>
  );
};

export const PortalContacts = ({ properties = [], propertyId }) => {
  const headers = useHeaders();
  const { toast, openDrawer } = usePortal();
  const [all, reload] = useLoad('/api/portal/contacts');
  const [moving, setMoving] = useState(false);
  const list = (all || []).filter((c) => !propertyId || c.scope === 'all' || c.address_ids.includes(propertyId));
  const edit = (c) => openDrawer({ title: c ? 'Edit additional contact' : 'Add additional contact', content: <ContactEditor contact={c} scope={propertyId} properties={properties} onSaved={reload} /> });
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.full_name} as a contact?`)) return;
    try { await axios.delete(`${API}/api/portal/contacts/${c.id}`, { headers }); toast('Contact removed.'); reload(); } catch (e) { toast(errText(e)); }
  };
  const up = async (c) => {
    const ids = all.map((x) => x.id);
    const prev = list[list.indexOf(c) - 1];
    const i = ids.indexOf(c.id); const j = ids.indexOf(prev.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setMoving(true);
    try { await axios.post(`${API}/api/portal/contacts/reorder`, { ids }, { headers }); reload(); } catch (e) { toast(errText(e)); } finally { setMoving(false); }
  };
  return (
    <section className="panel" style={{ marginTop: 22 }} data-testid="portal-contacts">
      <div className="section-top"><div><span className="eyebrow">WHEN WE NEED ANOTHER WAY TO REACH YOU</span><h2>Additional Contacts</h2></div>
        <button className="outline" onClick={() => edit(null)} data-testid="contact-add">＋ Add contact</button></div>
      <p>A spouse, co-host, property manager, or someone else we can contact if you’re unavailable. We’ll try you first, then these contacts in the order shown.</p>
      {list.length ? list.map((c) => (
        <article className="contact-entry" key={c.id} data-testid={`contact-${c.id}`}>
          <div className="section-top"><div><h3>{c.full_name}</h3><p>{c.relationship} · {scopeLabel(c, properties)}</p></div><Badge>Backup contact</Badge></div>
          <div className="detail-list">
            {c.phone && <div><small>Phone</small><strong>{c.phone}</strong></div>}
            {c.email && <div><small>Email</small><strong>{c.email}</strong></div>}
            <div><small>Preferred contact</small><strong>{METHOD[c.preferred_method]}</strong></div>
            <div><small>When to contact</small><strong>{c.instructions || 'If the primary client is unavailable.'}</strong></div>
          </div>
          <div className="inline-actions">
            <button className="text-button" onClick={() => edit(c)} data-testid={`contact-edit-${c.id}`}>Edit</button>
            {list.indexOf(c) > 0 && <button className="text-button" disabled={moving} onClick={() => up(c)} data-testid={`contact-up-${c.id}`}>Move earlier ↑</button>}
            <button className="text-button" onClick={() => remove(c)} data-testid={`contact-remove-${c.id}`}>Remove</button>
          </div>
        </article>
      )) : <div className="empty"><p>No additional contacts yet.</p><p>Add someone your team can reach when you’re unavailable.</p></div>}
      <div className="notice">These are backup contact details only. Adding someone does not give them portal access, access to invoices, or permission to approve service or pricing changes.</div>
    </section>
  );
};

export default PortalContacts;
